import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '../../../../../components/ui/card';
import { Button } from '../../../../../components/ui/button';
import { Label } from '../../../../../components/ui/label';
import { Input } from '../../../../../components/ui/input';
import { Checkbox } from '../../../../../components/ui/checkbox';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../../../components/ui/select';
import { Settings, Save, RefreshCw, Grid, Filter, Target, Loader2 } from 'lucide-react';
import type { DashboardProject } from '../../../types/project';
import { useLimitsData } from '../../../../../redux/WeldData/hooks/useLimitsData';
import { useWeldData } from '../../../../../redux/WeldData/hooks/useWeldData';
import { useUpdateStatusConfiguration } from '../../../../../redux/WeldData/hooks/useLimitsData';
import { useProjectManager } from '../../../../../redux/WeldData/hooks/useProjectManager';
import { Notification } from '../../../../../components/ui/custom-notification';
import { ValidationErrorModal } from './ValidationErrorModal';
import type { ValidationError } from './ValidationErrorModal';
import {
  convertWeldDataValue,
  convertSetupValueToInches,
  getParameterUnitSuffix,
  getUnitFromSettings
} from '../../../../../utils/unitUtils';

interface StatusConfigurationProps {
  project: DashboardProject;
  selectedPipeSize: number;
  selectedPipe: any;
  isEditing?: boolean;
  setIsEditing?: (editing: boolean) => void;
  onSave?: () => void;
  onSaveComplete?: () => void;
  triggerSave?: boolean;
  onValidationErrorsChange?: (hasErrors: boolean, errors?: any[]) => void;
  onValidationFailure?: () => void;
  onJobPipeInfoChange?: (jobNumber: string, pipeName: string) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  isModalOpen?: boolean;
  canEdit?: boolean;
}

interface StatusParameter {
  id: string;
  parameter: string;
  enabled: boolean;
  [key: string]: string | boolean; // Allow dynamic properties for any pass group
}

interface LimitsDataItem {
  id: number;
  project_id: number;
  project_name: string;
  welding_system: string;
  job_number: string;
  pipe_name?: string;
  zone_name: string;
  pass_name: string;
  pass_group_name: string;
  current_max: number;
  current_min: number;
  volts_max: number;
  volts_min: number;
  wire_speed_max: number;
  wire_speed_min: number;
  travel_speed_max: number;
  travel_speed_min: number;
  oscillation_width_max: number;
  oscillation_width_min: number;
  true_energy_max: number | null;
  true_energy_min: number | null;
  heat_max: number | null;
  heat_min: number | null;
}

interface WeldDetail {
  id: number;
  project_id: number;
  project_name: string;
  welding_system: string;
  job_number: string;
  slope_in_time: number;
  slope_out_time: number;
  statusCalculationLevel: string;
}

export const StatusConfiguration: React.FC<StatusConfigurationProps> = ({ project, selectedPipeSize, selectedPipe, isEditing: externalIsEditing, setIsEditing: externalSetIsEditing, onSave, onSaveComplete, triggerSave, onValidationErrorsChange, onValidationFailure, onJobPipeInfoChange, onDirtyChange, isModalOpen, canEdit = true }) => {
  const [internalIsEditing, setInternalIsEditing] = useState(false);
  const isEditing = externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;
  const setIsEditing = externalSetIsEditing || setInternalIsEditing;
  const [currentUnit, setCurrentUnit] = useState<string>(getUnitFromSettings());
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [showValidationModal, setShowValidationModal] = useState(false);

  // Function to get dynamic parameters based on current unit system
  const getDynamicParameters = (): StatusParameter[] => {
    const isMetric = currentUnit === 'meters';

    return [
      {
        id: '1',
        parameter: 'Current (A)',
        enabled: true
      },
      {
        id: '2',
        parameter: 'Volts (V)',
        enabled: true
      },
      {
        id: '3',
        parameter: `Wire Speed (${isMetric ? 'mm' : 'in'}/min)`,
        enabled: true
      },
      {
        id: '4',
        parameter: `Travel Speed (${isMetric ? 'mm' : 'in'}/min)`,
        enabled: true
      },
      {
        id: '5',
        parameter: `Oscillation Width (${isMetric ? 'mm' : 'in'})`,
        enabled: true
      },
      {
        id: '6',
        parameter: `Heat (${getParameterUnitSuffix('heat')})`,
        enabled: true
      },
      {
        id: '7',
        parameter: `True Energy (${getParameterUnitSuffix('true_energy')})`,
        enabled: true
      }
    ];
  };

  // Helper function to map parameter names to API keys
  const getParameterApiKey = (parameterName: string): string | null => {
    if (parameterName.includes('Current')) return 'current';
    if (parameterName.includes('Volts')) return 'volts';
    if (parameterName.includes('Wire Speed')) return 'wire_speed';
    if (parameterName.includes('Travel Speed')) return 'travel_speed';
    if (parameterName.includes('Oscillation Width')) return 'oscillation_width';
    if (parameterName.includes('Heat')) return 'heat';
    if (parameterName.includes('True Energy')) return 'true_energy';
    return null;
  };

  // Function to get parameter key from dynamic parameter name
  const getParameterKey = (parameterName: string): string => {
    // Use the helper function and convert null to empty string for backward compatibility
    return getParameterApiKey(parameterName) || '';
  };

  // Convert value from inches (API) to display unit
  const convertValueForDisplay = (value: number, parameter: string): number => {
    if (typeof value !== 'number' || isNaN(value)) return 0;

    // Skip conversion for electrical parameters - they're already in correct units (A and V)
    // Electrical parameters: Current (A), Volts (V) - NO CONVERSION NEEDED
    if (parameter.includes('Current') || parameter.includes('Volts')) {
      return value;
    }

    // Only convert physical measurements (wire speed, travel speed, oscillation width, heat, true energy)
    // Physical parameters: Wire Speed, Travel Speed, Oscillation Width, Heat, True Energy
    // These are stored in inches in the API and need conversion to mm for metric display
    // 
    // IMPORTANT: This function is ONLY called when:
    // 1. Loading data from API (inches → user's preferred unit)
    // 2. NOT when user is typing in input fields (user input stays as-is)
    const converted = convertWeldDataValue(value, parameter);
    return converted;
  };

  // Convert value from display unit back to inches for API
  const convertValueToInches = (value: number, parameter: string): number => {
    if (typeof value !== 'number' || isNaN(value)) return 0;

    // Skip conversion for electrical parameters - they're already in correct units (A and V)
    if (parameter.includes('Current') || parameter.includes('Volts')) {
      return value;
    }

    const displayUnit = getUnitFromSettings();

    // For energy parameters (heat, true_energy), convert from kJ/mm to kJ/in
    if (parameter.includes('Heat') || parameter.includes('True Energy')) {
      if (displayUnit === 'meters') {
        return value * 25.4; // Convert kJ/mm to kJ/in
      }
      return value; // Already in kJ/in
    }

    // For physical measurements, convert from mm to inches
    if (displayUnit === 'meters') {
      return value / 25.4; // Convert mm to inches
    }

    return value; // Already in inches
  };

  // Format value with proper decimal places based on parameter type
  const formatValueForDisplay = (value: number, parameter: string): string => {
    if (typeof value !== 'number' || isNaN(value)) return '';

    const convertedValue = convertValueForDisplay(value, parameter);

    // Apply different decimal precision based on parameter type using dynamic parameter names
    if (parameter.includes('Current') || parameter.includes('Volts')) {
      return convertedValue.toFixed(1); // 1 decimal place for electrical parameters
    } else if (parameter.includes('Wire Speed') || parameter.includes('Travel Speed')) {
      return convertedValue.toFixed(2); // 2 decimal places for speed parameters
    } else if (parameter.includes('Oscillation Width')) {
      return convertedValue.toFixed(3); // 3 decimal places for precision measurements
    } else if (parameter.includes('Heat') || parameter.includes('True Energy')) {
      return convertedValue.toFixed(2); // 2 decimal places for energy parameters
    } else {
      return convertedValue.toFixed(2); // Default 2 decimal places
    }
  };

  // Format value for API with 6 decimal places for precision
  const formatValueForAPI = (value: number, parameter: string): number => {
    if (typeof value !== 'number' || isNaN(value)) return 0;

    const inchesValue = convertValueToInches(value, parameter);

    // Return as number for API calls (matching curl command format)
    return inchesValue;
  };

  // Update parameters when unit system changes
  useEffect(() => {
    const newUnit = getUnitFromSettings();
    if (newUnit !== currentUnit) {
      setCurrentUnit(newUnit);
      setParameters(getDynamicParameters());
    }
  }, [currentUnit]);

  const [parameters, setParameters] = useState<StatusParameter[]>(() => {
    const initialParams = getDynamicParameters();
    return initialParams;
  });
  const [slopeTimeIn, setSlopeTimeIn] = useState('0');
  const [slopeTimeOut, setSlopeTimeOut] = useState('0');
  const [statusCalculationMethod, setStatusCalculationMethod] = useState('Average Zone');
  const [calculationLevel, setCalculationLevel] = useState('Pass Level');
  const [selectedJobNumber, setSelectedJobNumber] = useState<string>('');

  // State to track if we've already loaded data for a job number
  const [loadedJobs, setLoadedJobs] = useState<Set<string>>(new Set());

  // Local loading state for save button - always start as false
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Track if save button has been clicked to disable it
  const [saveButtonClicked, setSaveButtonClicked] = useState(false);

  // Track when we're waiting for data reload after save
  const [waitingForReload, setWaitingForReload] = useState(false);

  // Force reset saving state immediately on component mount
  React.useEffect(() => {
    setIsSaving(false);
    setSaveButtonClicked(false); // Reset button click state on mount
  }, []);

  // State to store parameter values for each job to prevent resetting when switching
  const [jobParameterStates, setJobParameterStates] = useState<Record<string, any>>({});

  // Function to reset loaded jobs (useful when data is refreshed)
  const resetLoadedJobs = () => {
    setLoadedJobs(new Set());
  };

  // Function to refresh data after saving (clears loaded jobs to force fresh data load)
  const refreshAfterSave = () => {
    resetLoadedJobs();

    // Force a refresh of the current job data
    if (selectedJobNumber) {
      // This will trigger the useEffects to reload data
    }
  };

  // State to store configurations for each job
  const [jobConfigurations, setJobConfigurations] = useState<Record<string, {
    parameters: StatusParameter[];
    slopeTimeIn: string;
    slopeTimeOut: string;
    statusCalculationMethod: string;
    calculationLevel: string;
    lastUpdated: number;
  }>>({});

  // Helper function to get configuration key based on welding system
  // For m500: use job_number:pipe_name combination
  // For others: use just job_number
  const getConfigurationKey = (jobNumberValue: string): string => {
    if (!jobNumberValue) return '';

    const [jobNum, pipeName] = jobNumberValue.split(':');

    // Find the welding system for this job
    const jobOption = jobNumbers.find(job => job.value === jobNumberValue);
    if (jobOption) {
      // Extract welding system from label (format: "welding_system: jobNum: pipeName")
      const labelParts = jobOption.label.split(':');
      const weldingSystem = labelParts[0]?.trim().toLowerCase() || '';

      // For m500, use job_number:pipe_name combination
      if (weldingSystem === 'm500' && pipeName) {
        return `${jobNum}:${pipeName}`;
      }
    }

    // For other welding systems or if no pipe name, use just job_number
    return jobNum || '';
  };

  // Function to save current job configuration
  const saveCurrentJobConfiguration = (jobNumber: string) => {
    if (jobNumber) {
      const configKey = getConfigurationKey(jobNumber);

      const currentConfig = {
        parameters: parameters.map(param => ({ ...param })),
        slopeTimeIn,
        slopeTimeOut,
        statusCalculationMethod,
        calculationLevel,
        lastUpdated: Date.now()
      };

      setJobConfigurations(prev => ({
        ...prev,
        [configKey]: currentConfig
      }));

    }
  };

  // Function to restore job configuration
  const restoreJobConfiguration = (jobNumber: string) => {
    const configKey = getConfigurationKey(jobNumber);
    const savedConfig = jobConfigurations[configKey];
  //  console.log('savedConfig', savedConfig);
    if (savedConfig) {
      setParameters(savedConfig.parameters);
      setSlopeTimeIn(savedConfig.slopeTimeIn);
      setSlopeTimeOut(savedConfig.slopeTimeOut);
      setStatusCalculationMethod(savedConfig.statusCalculationMethod);
      setCalculationLevel(savedConfig.calculationLevel);
      return true;
    }
    return false;
  };

  // Redux hooks
  const { limitsData, limitsLoading, loadLimitsData } = useLimitsData();
  const {
    projects,
    projectsLoading,
    findProjectByIdentifier,
    loading: weldDataLoading
  } = useWeldData();
  const { projectId, projectName, isProjectReady } = useProjectManager();
  const { updateStatusConfiguration, loading: updateLoading, reset: resetUpdateStatus } = useUpdateStatusConfiguration();

  // // Reset update status when component unmounts
  // useEffect(() => {
  //   return () => {
  //     resetLoadedJobs();
  //     resetUpdateStatus();
  //   };
  // }, [resetUpdateStatus]);

  // // Reset loading states when component mounts or modal opens
  // useEffect(() => {
  //   // Force reset local saving state first
  //   setIsSaving(false);
  //   setSaveButtonClicked(false); // Reset button click state
  //   // Reset all loading states when component mounts
  //   resetUpdateStatus();
  //   // Reset editing state to false when component mounts
  //   if (externalSetIsEditing) {
  //     externalSetIsEditing(false);
  //   }
  //   // Reset validation state
  //   setValidationErrors([]);
  //   setShowValidationModal(false);

  //   // Add multiple timeout-based resets as fallback for Redux state
  //   const timeoutId1 = setTimeout(() => {
  //     setIsSaving(false);
  //     setSaveButtonClicked(false);
  //     resetUpdateStatus();
  //   }, 100);

  //   const timeoutId2 = setTimeout(() => {
  //     setIsSaving(false);
  //     setSaveButtonClicked(false);
  //     resetUpdateStatus();
  //   }, 500);

  //   const timeoutId3 = setTimeout(() => {
  //     setIsSaving(false);
  //     setSaveButtonClicked(false);
  //     resetUpdateStatus();
  //   }, 1000);

  //   return () => {
  //     clearTimeout(timeoutId1);
  //     clearTimeout(timeoutId2);
  //     clearTimeout(timeoutId3);
  //   };
  // }, [resetUpdateStatus, externalSetIsEditing]);

  // // Reset loading states when job number changes
  // useEffect(() => {
  //   // Reset loading states when switching jobs
  //   setIsSaving(false);
  //   setSaveButtonClicked(false); // Reset button click state when switching jobs
  //   resetUpdateStatus();
  // }, [selectedJobNumber, resetUpdateStatus]);

  // // Simple timeout-based reset to ensure button is always re-enabled
  // useEffect(() => {
  //   if (isSaving) {
  //     // Force reset after 30 seconds maximum to prevent stuck states
  //     const timeoutId = setTimeout(() => {
  //       setIsSaving(false);
  //       setSaveButtonClicked(false);
  //     }, 30000);

  //     return () => clearTimeout(timeoutId);
  //   }
  // }, [isSaving]);

  // Simple loading state - show loader only when actively saving
  const isActuallyLoading = isSaving;

  // Watch for external save trigger
  useEffect(() => {
    if (triggerSave && isEditing) {
      handleSave();
    }
  }, [triggerSave, isEditing]);

  // Notify parent when validation errors change
  useEffect(() => {
    if (onValidationErrorsChange) {
      onValidationErrorsChange(validationErrors.length > 0, validationErrors);
    }
  }, [validationErrors, onValidationErrorsChange]);

  useEffect(() => {
    if (isModalOpen) {
      setIsDirty(false);
    }
  }, [isModalOpen]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // Function to get unique job numbers with welding system and pipe name
  const getUniqueJobNumbers = (data: any[], weldDetails: any[]) => {
    if (!data || data.length === 0) return [];

    const uniqueJobNumbers = new Set<string>();
    data.forEach((item: any) => {
      if (item.job_number) {
        uniqueJobNumbers.add(`${item.job_number}:${item.pipe_name ?? ""}`);
      }
    });

    return Array.from(uniqueJobNumbers)
      .filter((jobNumber) => jobNumber !== null && jobNumber.split(":")[0])
      .map((job_number) => {
        const [jobNum, pipeName = ""] = job_number.split(":");

        // Try to find welding_system from weldDetails first, then fallback to tableLimitData
        let welding_system = "";
        const weldDetail = weldDetails?.find((wld: any) => wld.job_number == jobNum);
        if (weldDetail?.welding_system) {
          welding_system = weldDetail.welding_system.replace(/\W/g, "");
        } else {
          // Fallback: get welding_system from tableLimitData
          const tableItem = data.find(
            (item: any) =>
              item.job_number === jobNum &&
              (pipeName
                ? item.pipe_name === pipeName
                : item.pipe_name == null || item.pipe_name === "null")
          );
          welding_system = tableItem?.welding_system?.replace(/\W/g, "") || "";
        }

        return {
          label: `${welding_system}: ${jobNum}${pipeName ? `: ${pipeName}` : ""}`,
          pipeName: pipeName,
          jobNumber: jobNum,
          value: job_number,
        };
      });
  };

  // Get unique job numbers from limits data with enhanced display
  const jobNumbers = React.useMemo(() => {
    if (!limitsData?.allDataSet) return [];

    return getUniqueJobNumbers(limitsData.allDataSet, limitsData?.weldDetails);
  }, [limitsData]);

  // Filter data based on selected job number
  const filteredData = React.useMemo(() => {
    if (!limitsData?.allDataSet || !selectedJobNumber) return [];

    // Extract job number and pipe name from the selected value
    const [jobNum, pipeName] = selectedJobNumber.split(':');

    return limitsData.allDataSet.filter((item: LimitsDataItem) => {
      const matchesJobNumber = item.job_number === jobNum;
      const matchesPipeName = pipeName
        ? item.pipe_name === pipeName
        : (item.pipe_name == null || item.pipe_name === "null" || item.pipe_name === "");

      return matchesJobNumber && matchesPipeName;
    });
  }, [limitsData, selectedJobNumber]);

  // // Group data by pass group name
  // const groupedData = React.useMemo(() => {
  //   if (!filteredData.length) return {};

  //   const grouped: Record<string, LimitsDataItem[]> = {};
  //   filteredData.forEach((item: LimitsDataItem) => {
  //     const passGroup = item.pass_group_name || 'Unknown';
  //     if (!grouped[passGroup]) {
  //       grouped[passGroup] = [];
  //     }
  //     grouped[passGroup].push(item);
  //   });

  //   return grouped;
  // }, [filteredData]);

  // Get unique pass groups for the selected job number
  const uniquePassGroups = React.useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];

    const passGroups = new Set<string>();
    filteredData.forEach((item: LimitsDataItem) => {
      if (item.pass_group_name) {
        passGroups.add(item.pass_group_name);
      }
    });

    // Sort pass groups using PASS_GROUP_ORDER logic with case-insensitive comparison
    const PASS_GROUP_ORDER = [
      "root",
      "hot",
      "hot pass",
      "fill",
      "last fill",
      "strip",
      "cap",
    ];

    return Array.from(passGroups).sort((a, b) => {
      // Convert to lowercase for comparison
      const aLower = a.toLowerCase();
      const bLower = b.toLowerCase();

      // Find index in predefined order
      const indexA = PASS_GROUP_ORDER.indexOf(aLower);
      const indexB = PASS_GROUP_ORDER.indexOf(bLower);

      // Both items are in the predefined order - sort by their position
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;

      // Only item A is in the predefined order - A comes first
      if (indexA !== -1) return -1;

      // Only item B is in the predefined order - B comes first  
      if (indexB !== -1) return 1;

      // Neither item is in the predefined order - sort alphabetically
      return a.localeCompare(b);
    });
  }, [filteredData]);

  // Load limits data when component mounts
  useEffect(() => {
    if (project?.project?.projectNumber && projects && projects.length > 0) {

      // Use centralized project data for limits API calls
      loadLimitsData(); // No parameters needed - uses centralized project data
    } else if (project?.project?.projectId) {
      // Fallback: load limits data using centralized project manager
      loadLimitsData(); // No parameters needed - uses centralized project data
    }
  }, [project?.project?.projectNumber, project?.project?.projectId, projects?.length, loadLimitsData, findProjectByIdentifier]);

  // // Wait for data reload to complete after save, then update UI
  // useEffect(() => {
  //   if (waitingForReload && !limitsLoading && limitsData) {
  //   console.log('waitingForReload', waitingForReload);
  //     // Data has finished loading, clear loaded jobs to force UI refresh
  //     // This will trigger the slope time useEffect to reload from API
  //     resetLoadedJobs();
  //     setWaitingForReload(false);

  //     // Force update slope values from fresh API data for current job
  //     if (selectedJobNumber && limitsData?.weldDetails) {
  //       const jobNum = selectedJobNumber.split(':')[0];
  //       const weldDetail = limitsData.weldDetails.find(
  //         (detail: WeldDetail) => detail.job_number === jobNum
  //       );

  //       if (weldDetail) {
  //         setSlopeTimeIn(weldDetail.slope_in_time?.toString() || '0');
  //         setSlopeTimeOut(weldDetail.slope_out_time?.toString() || '0');
  //       }
  //     }

  //     // Call onSaveComplete callback to notify parent that API call is done
  //     if (onSaveComplete) {
  //       onSaveComplete();
  //     }
  //   }
  // }, [waitingForReload, limitsLoading, limitsData, resetLoadedJobs, onSaveComplete, selectedJobNumber]);

  // Set first job number as default when data loads
  useEffect(() => {
    if (jobNumbers.length > 0 && !selectedJobNumber) {
   //   console.log('jobNumbers', jobNumbers);

      setSelectedJobNumber(jobNumbers[0].value);
    }
  }, [jobNumbers, selectedJobNumber]);

  // Clear validation errors when no job is selected
  useEffect(() => {
    if (!selectedJobNumber || filteredData.length === 0) {
      if (validationErrors.length > 0) {
        setValidationErrors([]);
        setShowValidationModal(false);
      }
    }
  }, [selectedJobNumber, filteredData.length, validationErrors.length]);

  // Notify parent component when job or pipe information changes
  useEffect(() => {
    //console.log('selectedJobNumber', selectedJobNumber);
    if (selectedJobNumber && filteredData.length > 0 && onJobPipeInfoChange) {
      const pipeName = filteredData[0]?.pipe_name || 'Unknown';
      onJobPipeInfoChange(selectedJobNumber, pipeName);
    }
  }, [selectedJobNumber, filteredData, onJobPipeInfoChange]);

  // Update parameters when filtered data changes
  useEffect(() => {
    if (filteredData.length > 0) {
      // If job is already loaded, skip this update to prevent infinite loop
      if (selectedJobNumber && loadedJobs.has(selectedJobNumber)) {
        return;
      }

      // Check if we have a saved state for this job using configuration key
      const configKey = selectedJobNumber ? getConfigurationKey(selectedJobNumber) : '';
      const hasSavedState = configKey && jobParameterStates[configKey];

      if (hasSavedState) {
        // Restore the saved state instead of loading from API
        restoreJobState(selectedJobNumber);
        // Mark as loaded to prevent future API updates
        setLoadedJobs(prev => new Set(Array.from(prev).concat(selectedJobNumber)));
        updateLoadedJobsState(selectedJobNumber);
        return;
      }

      // First time loading - create fresh parameters
      const updatedParameters = getDynamicParameters().map(param => {
        const newParam = { ...param };

        // Group data by pass_group_name
        const passGroups = filteredData.reduce((acc: Record<string, LimitsDataItem[]>, item: LimitsDataItem) => {
          if (!acc[item.pass_group_name]) {
            acc[item.pass_group_name] = [];
          }
          acc[item.pass_group_name].push(item);
          return acc;
        }, {} as Record<string, LimitsDataItem[]>);

        // Update parameters based on pass groups
        Object.entries(passGroups).forEach(([passGroup, items]) => {
          const typedItems = items as LimitsDataItem[];
          if (typedItems.length > 0) {
            // Dynamically set values based on pass group
            const passGroupKey = passGroup.toLowerCase().replace(/\s+/g, '');
            const minKey = `${passGroupKey}Min`;
            const maxKey = `${passGroupKey}Max`;

            // Use parameter name matching to determine which values to set
            if (param.parameter.includes('Current')) {
              const currentMins = typedItems.map((item: LimitsDataItem) => item.current_min).filter(Boolean);
              const currentMaxs = typedItems.map((item: LimitsDataItem) => item.current_max).filter(Boolean);
              const minValue = currentMins.length > 0 ? Math.min(...currentMins) : 0;
              const maxValue = currentMaxs.length > 0 ? Math.max(...currentMaxs) : 0;
              const formattedMin = formatValueForDisplay(minValue, param.parameter);
              const formattedMax = formatValueForDisplay(maxValue, param.parameter);
              newParam[minKey] = formattedMin;
              newParam[maxKey] = formattedMax;
            } else if (param.parameter.includes('Volts')) {
              const voltsMins = typedItems.map((item: LimitsDataItem) => item.volts_min).filter(Boolean);
              const voltsMaxs = typedItems.map((item: LimitsDataItem) => item.volts_max).filter(Boolean);
              const minValue = voltsMins.length > 0 ? Math.min(...voltsMins) : 0;
              const maxValue = voltsMaxs.length > 0 ? Math.max(...voltsMaxs) : 0;
              const formattedMin = formatValueForDisplay(minValue, param.parameter);
              const formattedMax = formatValueForDisplay(maxValue, param.parameter);
              newParam[minKey] = formattedMin;
              newParam[maxKey] = formattedMax;
            } else if (param.parameter.includes('Wire Speed')) {
              const wireSpeedMins = typedItems.map((item: LimitsDataItem) => item.wire_speed_min).filter(Boolean);
              const wireSpeedMaxs = typedItems.map((item: LimitsDataItem) => item.wire_speed_max).filter(Boolean);
              const minValue = wireSpeedMins.length > 0 ? Math.min(...wireSpeedMins) : 0;
              const maxValue = wireSpeedMaxs.length > 0 ? Math.max(...wireSpeedMaxs) : 0;
              const formattedMin = formatValueForDisplay(minValue, param.parameter);
              const formattedMax = formatValueForDisplay(maxValue, param.parameter);
              newParam[minKey] = formattedMin;
              newParam[maxKey] = formattedMax;
            } else if (param.parameter.includes('Travel Speed')) {
              const travelSpeedMins = typedItems.map((item: LimitsDataItem) => item.travel_speed_min).filter(Boolean);
              const travelSpeedMaxs = typedItems.map((item: LimitsDataItem) => item.travel_speed_max).filter(Boolean);
              const minValue = travelSpeedMins.length > 0 ? Math.min(...travelSpeedMins) : 0;
              const maxValue = travelSpeedMaxs.length > 0 ? Math.max(...travelSpeedMaxs) : 0;
              const formattedMin = formatValueForDisplay(minValue, param.parameter);
              const formattedMax = formatValueForDisplay(maxValue, param.parameter);
              newParam[minKey] = formattedMin;
              newParam[maxKey] = formattedMax;
            } else if (param.parameter.includes('Oscillation Width')) {
              const oscillationMins = typedItems.map((item: LimitsDataItem) => item.oscillation_width_min).filter(Boolean);
              const oscillationMaxs = typedItems.map((item: LimitsDataItem) => item.oscillation_width_max).filter(Boolean);
              const minValue = oscillationMins.length > 0 ? Math.min(...oscillationMins) : 0;
              const maxValue = oscillationMaxs.length > 0 ? Math.max(...oscillationMaxs) : 0;
              const formattedMin = formatValueForDisplay(minValue, param.parameter);
              const formattedMax = formatValueForDisplay(maxValue, param.parameter);
              newParam[minKey] = formattedMin;
              newParam[maxKey] = formattedMax;
            } else if (param.parameter.includes('Heat')) {
              const heatMins = typedItems.map((item: LimitsDataItem) => item.heat_min).filter(val => val !== null) as number[];
              const heatMaxs = typedItems.map((item: LimitsDataItem) => item.heat_max).filter(val => val !== null) as number[];
              const minValue = heatMins.length > 0 ? Math.min(...heatMins) : 0;
              const maxValue = heatMaxs.length > 0 ? Math.max(...heatMaxs) : 0;
              const formattedMin = formatValueForDisplay(minValue, param.parameter);
              const formattedMax = formatValueForDisplay(maxValue, param.parameter);
              newParam[minKey] = formattedMin;
              newParam[maxKey] = formattedMax;
            } else if (param.parameter.includes('True Energy')) {
              const trueEnergyMins = typedItems.map((item: LimitsDataItem) => item.true_energy_min).filter(val => val !== null) as number[];
              const trueEnergyMaxs = typedItems.map((item: LimitsDataItem) => item.true_energy_max).filter(val => val !== null) as number[];
              const minValue = trueEnergyMins.length > 0 ? Math.min(...trueEnergyMins) : 0;
              const maxValue = trueEnergyMaxs.length > 0 ? Math.max(...trueEnergyMaxs) : 0;
              const formattedMin = formatValueForDisplay(minValue, param.parameter);
              const formattedMax = formatValueForDisplay(maxValue, param.parameter);
              newParam[minKey] = formattedMin;
              newParam[maxKey] = formattedMax;
            }
          }
        });

        return newParam;
      });

      setParameters(updatedParameters);

      // Mark this job as loaded after successfully updating parameters
      if (selectedJobNumber) {
        setLoadedJobs(prev => new Set(Array.from(prev).concat(selectedJobNumber)));
        updateLoadedJobsState(selectedJobNumber);
      }
    }
  }, [filteredData, selectedJobNumber, loadedJobs, jobParameterStates]);

  // Update parameter checkbox states when limits data is loaded and job number is selected
  useEffect(() => {
    if (limitsData?.weldDetails && selectedJobNumber) {
      // First, check if we have a previously saved configuration for this job
      const hasRestoredConfig = restoreJobConfiguration(selectedJobNumber);

      if (hasRestoredConfig) {
        return;
      }

      // Extract job number and pipe name from the selected value (format: job_number:pipe_name)
      const [jobNum, pipeNameFromValue] = selectedJobNumber.split(':');
      
      // Determine welding system from jobNumbers
      const jobOption = jobNumbers.find(job => job.value === selectedJobNumber);
      const weldingSystem = jobOption?.label.split(':')[0]?.trim().toLowerCase() || '';
      const isM500 = weldingSystem === 'm500';

      // Find the job data from the GET limits API response
      // For m500: match by job_number and pipe_name
      // For others: match by job_number only
      let jobWeldDetails: any;
      
      if (isM500 && pipeNameFromValue) {
        // For m500, match by both job_number and pipe_name
        jobWeldDetails = limitsData.weldDetails.find((item: any) => {
          if (item.job_number === jobNum) {
            // If weldDetail has pipe_name, match it; otherwise just match job_number
            return item.pipe_name ? item.pipe_name === pipeNameFromValue : true;
          }
          return false;
        });
        
        // If not found (weldDetails might not have pipe_name), use the first match for this job_number
        if (!jobWeldDetails) {
          jobWeldDetails = limitsData.weldDetails.find((item: any) => item.job_number === jobNum);
        }
      } else {
        // For other welding systems, match by job_number only
        jobWeldDetails = limitsData.weldDetails.find((item: any) => item.job_number === jobNum);
      }

      if (jobWeldDetails?.selected_parameter_list) {
        try {
          // Parse the selected_parameter_list from the API response
          const apiSelectedParams = typeof jobWeldDetails.selected_parameter_list === 'string'
            ? JSON.parse(jobWeldDetails.selected_parameter_list)
            : jobWeldDetails.selected_parameter_list;



          setParameters(prev => prev.map(param => {
            const newParam = { ...param };
            const apiKey = getParameterApiKey(param.parameter);

            if (apiKey && apiSelectedParams.hasOwnProperty(apiKey)) {
              newParam.enabled = apiSelectedParams[apiKey] || false;
            }

            return newParam;
          }));

          setSlopeTimeIn(jobWeldDetails.slope_in_time?.toString() || '0');
          setSlopeTimeOut(jobWeldDetails.slope_out_time?.toString() || '0');
        //  setStatusCalculationMethod(jobWeldDetails.status_calculation_method || 'Pass Level');
          //setCalculationLevel(jobWeldDetails.status_calculation_level || 'Pass Level');

        } catch (error) {
          console.error('Error parsing selected_parameter_list:', error);
        }
      } else {
        // No selected_parameter_list found in API for this job
      }
    }
  }, [limitsData?.weldDetails, selectedJobNumber, jobConfigurations, jobNumbers]);

  // Update slope time and status calculation method when job number changes
  useEffect(() => {
   // console.log('selectedJobNumber', selectedJobNumber, jobConfigurations);
    if (selectedJobNumber && limitsData?.weldDetails) {
      // Extract job number from the selected value (format: job_number:pipe_name)
      const jobNum = selectedJobNumber.split(':')[0];

      //console.log('jobNum', jobNum);
      // Get configuration key based on welding system
      const configKey = getConfigurationKey(selectedJobNumber);
     // console.log('configKey', configKey);

      // First, check if we have a previously saved configuration for this job
      const savedConfig = jobConfigurations[configKey];
    //  console.log('savedConfig', savedConfig);
      if (savedConfig) {
        // Restore slope values from saved configuration
        setSlopeTimeIn(savedConfig.slopeTimeIn);
        setSlopeTimeOut(savedConfig.slopeTimeOut);
        setStatusCalculationMethod(savedConfig.statusCalculationMethod);
        setCalculationLevel(savedConfig.calculationLevel);
        setParameters(savedConfig.parameters);
        return;
      }

      // If no saved configuration and job already loaded, skip to prevent overwriting user changes
      if (loadedJobs.has(selectedJobNumber)) {
        return;
      }

      // Load slope values from API data
      // For m500: match by job_number and pipe_name combination (if pipe_name exists in weldDetails)
      // For others: match by job_number only
      const [jobNumberFromValue, pipeNameFromValue] = selectedJobNumber.split(':');
      const jobOption = jobNumbers.find(job => job.value === selectedJobNumber);
      const weldingSystem = jobOption?.label.split(':')[0]?.trim().toLowerCase() || '';

      let weldDetail: WeldDetail | undefined;

      if (weldingSystem === 'm500' && pipeNameFromValue) {
        // For m500, try to find weldDetail matching both job_number and pipe_name
        // Check if weldDetails has pipe_name property
        weldDetail = limitsData.weldDetails.find(
          (detail: any) => {
            if (detail.job_number === jobNum) {
              // If weldDetail has pipe_name, match it; otherwise just match job_number
              return detail.pipe_name ? detail.pipe_name === pipeNameFromValue : true;
            }
            return false;
          }
        );

        // If not found (weldDetails might not have pipe_name), use the first match for this job_number
        if (!weldDetail) {
          weldDetail = limitsData.weldDetails.find(
            (detail: WeldDetail) => detail.job_number === jobNum
          );
        }
      } else {
        // For other welding systems, match by job_number only
        weldDetail = limitsData.weldDetails.find(
          (detail: WeldDetail) => detail.job_number === jobNum
        );
      }

      if (weldDetail) {
        setSlopeTimeIn(weldDetail.slope_in_time?.toString() || '0');
        setSlopeTimeOut(weldDetail.slope_out_time?.toString() || '0');
        // Use statusCalculationMethod from projectData, not from weldDetails
        setStatusCalculationMethod(limitsData?.projectData?.statusCalculationMethod || 'Pass Level');

        // Mark this job as loaded after successfully updating slope time
        setLoadedJobs(prev => new Set(Array.from(prev).concat(selectedJobNumber)));
        updateLoadedJobsState(selectedJobNumber);
      }
    }
  }, [selectedJobNumber, limitsData, loadedJobs, jobConfigurations, jobNumbers]);

  // Initialize status calculation method from project data when component mounts
  useEffect(() => {
    if (limitsData?.projectData?.statusCalculationMethod) {
      setStatusCalculationMethod(limitsData.projectData.statusCalculationMethod);
    }
  }, [limitsData?.projectData?.statusCalculationMethod]);

  // Console log the current configuration values
  useEffect(() => {
    // Configuration values updated - no logging needed
  }, [slopeTimeIn, slopeTimeOut, statusCalculationMethod, calculationLevel, selectedJobNumber, limitsData?.projectData?.statusCalculationMethod]);

  // // Console log when job number changes
  // useEffect(() => {
  //   if (selectedJobNumber) {
  //     // Job number changed - no logging needed
  //   }
  // }, [selectedJobNumber, uniquePassGroups]);

  // // Console log when limits data is loaded
  // useEffect(() => {
  //   // Limits data loaded - no logging needed
  // }, [limitsData]);

  // // Console log when parameters are updated
  // useEffect(() => {
  //   // Parameters updated - no logging needed
  // }, []);

  // Function to save current job's parameter state before switching
  const saveCurrentJobState = () => {
    if (selectedJobNumber && parameters.length > 0) {
      const configKey = getConfigurationKey(selectedJobNumber);

      const currentState = parameters.reduce((acc, param) => {
        // Extract all pass group values for this parameter
        const passGroupValues: any = {};
        Object.keys(param).forEach(key => {
          if (key.includes('Min') || key.includes('Max')) {
            passGroupValues[key] = param[key];
          }
        });

        acc[param.parameter] = {
          enabled: param.enabled,
          ...passGroupValues
        };
        return acc;
      }, {} as any);

      setJobParameterStates(prev => ({
        ...prev,
        [configKey]: currentState
      }));

      // Also save slope values and configuration to jobConfigurations
      saveCurrentJobConfiguration(selectedJobNumber);
    }
  };

  // Function to restore job's parameter state
  const restoreJobState = (jobNumber: string) => {
    const configKey = getConfigurationKey(jobNumber);
    const savedState = jobParameterStates[configKey];
    if (savedState && parameters.length > 0) {
      const restoredParameters = parameters.map(param => {
        const newParam = { ...param };
        const savedParamState = savedState[param.parameter];

        if (savedParamState) {
          newParam.enabled = savedParamState.enabled;
          // Restore all pass group values
          Object.keys(savedParamState).forEach(key => {
            if (key.includes('Min') || key.includes('Max')) {
              newParam[key] = savedParamState[key];
            }
          });
        }

        return newParam;
      });

      setParameters(restoredParameters);
    }
  };

  // Handle job number change with state persistence
  const handleJobNumberChange = (newJobNumber: string) => {
    // Clear validation errors when job number changes
    if (validationErrors.length > 0) {
      setValidationErrors([]);
      setShowValidationModal(false);
    }

    // Save current job's state and configuration before switching
    if (selectedJobNumber) {
      saveCurrentJobState();
      saveCurrentJobConfiguration(selectedJobNumber);
    }

    // Update selected job number
    setSelectedJobNumber(newJobNumber);

    // Restore the new job's configuration (includes slope values) if it exists
    const hasRestoredConfig = restoreJobConfiguration(newJobNumber);

    // If no saved configuration, restore parameter state if it exists
    const configKey = getConfigurationKey(newJobNumber);
    if (!hasRestoredConfig && jobParameterStates[configKey]) {
      restoreJobState(newJobNumber);
    }
  };

  const handleParameterChange = (id: string, field: keyof StatusParameter, value: string | boolean) => {
    setIsDirty(true);

    // Clear validation errors when parameters change
    if (validationErrors.length > 0) {
      setValidationErrors([]);
      setShowValidationModal(false);
    }

    setParameters(prev => {
      const updated = prev.map(param => {
        if (param.id === id) {
          // If this is a value field (min/max), store the user input as-is
          if (typeof value === 'string' && (field.toString().includes('Min') || field.toString().includes('Max'))) {
            const numericValue = parseFloat(value);
            if (!isNaN(numericValue)) {
              // Store the user input directly without conversion
              // Conversion will happen only when saving to API
              // This ensures user sees exactly what they typed
              return { ...param, [field]: value };
            }
          }
          // For non-value fields (like enabled), just update normally
          return { ...param, [field]: value };
        }
        return param;
      });


      return updated;
    });
  };

  // Wrapper functions to clear validation errors when configuration changes
  const handleSlopeTimeInChange = (value: string) => {
    setIsDirty(true);

    if (validationErrors.length > 0) {
      setValidationErrors([]);
      setShowValidationModal(false);
    }
    setSlopeTimeIn(value);
  };

  const handleSlopeTimeOutChange = (value: string) => {
    setIsDirty(true);

    if (validationErrors.length > 0) {
      setValidationErrors([]);
      setShowValidationModal(false);
    }
    setSlopeTimeOut(value);
  };

  const handleStatusCalculationMethodChange = (value: string) => {
    setIsDirty(true);

    if (validationErrors.length > 0) {
      setValidationErrors([]);
      setShowValidationModal(false);
    }
    setStatusCalculationMethod(value);
  };

  const handleCalculationLevelChange = (value: string) => {
    setIsDirty(true);

    if (validationErrors.length > 0) {
      setValidationErrors([]);
      setShowValidationModal(false);
    }
    setCalculationLevel(value);
  };

  // Function to check if a specific input field has validation errors
  const hasInputError = (parameterName: string, passGroup: string, fieldType: 'min' | 'max'): boolean => {
    return validationErrors.some(error =>
      error.parameter === parameterName &&
      error.passGroup === passGroup &&
      (fieldType === 'min' ? error.minValue : error.maxValue) !== undefined
    );
  };

  // Function to validate parameters before saving
  const validateParameters = (): ValidationError[] => {
    const errors: ValidationError[] = [];

    // Only validate the currently selected job - don't check all jobs in the project
    // This prevents validation errors for jobs that aren't being configured

    // Continue with other validations even if some jobs are missing parameters
    // This allows us to show ALL validation errors at once

    // Validate slope time values (0-10 range, can be negative)
    const slopeInValue = parseFloat(slopeTimeIn);
    const slopeOutValue = parseFloat(slopeTimeOut);

    // Check if slope values are within valid range (-10 to 10)
    if (slopeInValue < -10 || slopeInValue > 10) {
      errors.push({
        parameter: 'Slope In Time',
        minValue: slopeInValue,
        maxValue: slopeInValue,
        unit: 'sec',
        passGroup: 'Slope',
        type: 'out_of_range'
      });
    }

    if (slopeOutValue < -10 || slopeOutValue > 10) {
      errors.push({
        parameter: 'Slope Out Time',
        minValue: slopeOutValue,
        maxValue: slopeOutValue,
        unit: 'sec',
        passGroup: 'Slope',
        type: 'out_of_range'
      });
    }

    parameters.forEach(param => {
      // Only validate if the parameter is enabled (checkbox is checked)
      if (!param.enabled) {
        return; // Skip validation for disabled parameters
      }

      uniquePassGroups.forEach(passGroup => {
        const passGroupKey = passGroup.toLowerCase().replace(/\s+/g, '');
        const minKey = `${passGroupKey}Min`;
        const maxKey = `${passGroupKey}Max`;

        const minValue = parseFloat(String(param[minKey] || '0'));
        const maxValue = parseFloat(String(param[maxKey] || '0'));

        // Skip validation if both min and max values are 0 (treat as no limits set)
        if (minValue === 0 && maxValue === 0) {
          return; // Skip all validation for this parameter
        }

        // Check if min value is greater than or equal to max value
        if (minValue >= maxValue) {
          // Get parameter unit
          let unit = '';
          if (param.parameter.includes('Current')) unit = 'A';
          else if (param.parameter.includes('Volts')) unit = 'V';
          else if (param.parameter.includes('Wire Speed')) unit = currentUnit === 'meters' ? 'mm/min' : 'in/min';
          else if (param.parameter.includes('Travel Speed')) unit = 'in/min';
          else if (param.parameter.includes('Oscillation Width')) unit = 'in';
          else if (param.parameter.includes('Heat')) unit = getParameterUnitSuffix('heat');
          else if (param.parameter.includes('True Energy')) unit = getParameterUnitSuffix('true_energy');

          errors.push({
            parameter: param.parameter,
            minValue: minValue,
            maxValue: maxValue,
            unit: unit,
            passGroup: passGroup,
            type: 'min_max_mismatch'
          });
        }

        // Additional validation: check for negative values
        if (minValue < 0) {
          let unit = '';
          if (param.parameter.includes('Current')) unit = 'A';
          else if (param.parameter.includes('Volts')) unit = 'V';
          else if (param.parameter.includes('Wire Speed')) unit = currentUnit === 'meters' ? 'mm/min' : 'in/min';
          else if (param.parameter.includes('Travel Speed')) unit = 'in/min';
          else if (param.parameter.includes('Oscillation Width')) unit = 'in';
          else if (param.parameter.includes('Heat')) unit = getParameterUnitSuffix('heat');
          else if (param.parameter.includes('True Energy')) unit = getParameterUnitSuffix('true_energy');

          errors.push({
            parameter: param.parameter,
            minValue: minValue,
            maxValue: maxValue,
            unit: unit,
            passGroup: passGroup,
            type: 'negative_value'
          });
        }

        // Additional validation: check for max value being negative
        if (maxValue < 0) {
          let unit = '';
          if (param.parameter.includes('Current')) unit = 'A';
          else if (param.parameter.includes('Volts')) unit = 'V';
          else if (param.parameter.includes('Wire Speed')) unit = currentUnit === 'meters' ? 'mm/min' : 'in/min';
          else if (param.parameter.includes('Travel Speed')) unit = 'in/min';
          else if (param.parameter.includes('Oscillation Width')) unit = 'in';
          else if (param.parameter.includes('Heat')) unit = getParameterUnitSuffix('heat');
          else if (param.parameter.includes('True Energy')) unit = getParameterUnitSuffix('true_energy');

          errors.push({
            parameter: param.parameter,
            minValue: minValue,
            maxValue: maxValue,
            unit: unit,
            passGroup: passGroup,
            type: 'negative_value'
          });
        }
      });
    });


    return errors;
  };

  const handleSave = async () => {
    try {
      // Set local loading state
      setIsSaving(true);

      // Save current job's parameter state before validation
      if (selectedJobNumber) {
        saveCurrentJobState();
      }

      // Reset any previous update status
      resetUpdateStatus();

      // Validate parameters before saving
      const errors = validateParameters();
      if (errors.length > 0) {
        setValidationErrors(errors);
        setShowValidationModal(true);
        // Reset saving states when validation fails
        setIsSaving(false);
        setSaveButtonClicked(false);
        // Notify parent that save was attempted but validation failed
        if (onValidationFailure) {
          onValidationFailure();
        }
        return;
      }

      //console.log('jobNumbers', jobNumbers);
      //console.log('selectedJobNumber', selectedJobNumber);

      // Format data according to API structure - match the working curl command exactly
      //as api requires only one job at a time-
      const payload = jobNumbers.filter(jobOption => jobOption.value === selectedJobNumber).map(jobOption => {
      //  console.log('jobOption', jobOption);
        const jobNumber = jobOption.value.split(':')[0]; // Extract job number from the full value
        const pipeName = jobOption.value.split(':')[1];

        // Get welding system from jobOption label (format: "welding_system: jobNum: pipeName")
        const weldingSystem = jobOption.label.split(':')[0]?.trim().toLowerCase() || '';
        const isM500 = weldingSystem === 'm500';

        // For m500: match by job_number and pipe_name
        // For others (p625, etc.): match by job_number only, pipe_name should be null/empty
        const jobData = limitsData?.allDataSet?.find((item: LimitsDataItem) => {
          if (isM500 && pipeName) {
            return item.job_number === jobNumber && item.pipe_name === pipeName;
          } else {
            // For non-m500, pipe_name should be null, empty, or undefined
            return item.job_number === jobNumber &&
              (item.pipe_name == null || item.pipe_name === '' || item.pipe_name === 'null');
          }
        });

        // console.log('jobData', jobData);
        // console.log('pipeName', pipeName);
        // console.log('jobNumber', jobNumber);
        // console.log('weldingSystem', weldingSystem);
        // console.log('isM500', isM500);
        //console.log('limitsData?.allDataSet', limitsData?.allDataSet);

        // Get pass groups specific to THIS job number
        // For m500: filter by job_number and pipe_name
        // For others: filter by job_number only, pipe_name should be null/empty
        const jobSpecificPassGroups = limitsData?.allDataSet
          ?.filter((item: LimitsDataItem) => {
            if (isM500 && pipeName) {
              return item.job_number === jobNumber && item.pipe_name === pipeName;
            } else {
              // For non-m500, pipe_name should be null, empty, or undefined
              return item.job_number === jobNumber &&
                (item.pipe_name == null || item.pipe_name === '' || item.pipe_name === 'null');
            }
          })
          ?.map((item: LimitsDataItem) => item.pass_group_name)
          ?.filter((value: string | undefined): value is string => Boolean(value))
          ?.filter((value: string, index: number, self: string[]) => self.indexOf(value) === index) || [];



        // Get pass updates for this job - ONLY include pass groups that belong to this job
        const passUpdates = jobSpecificPassGroups.map((passGroup: string) => {

          // Create the pass update object with exact parameter names from the working curl
          const passUpdate: any = {
            pass_group_name: passGroup
          };

          // Get the actual data for this pass group from limits data
          // For m500: filter by job_number, pipe_name, and pass_group_name
          // For others: filter by job_number and pass_group_name, pipe_name should be null/empty
          const passGroupData = limitsData?.allDataSet?.filter((item: LimitsDataItem) => {
            if (isM500 && pipeName) {
              return item.job_number === jobNumber &&
                item.pipe_name === pipeName &&
                item.pass_group_name === passGroup;
            } else {
              // For non-m500, pipe_name should be null, empty, or undefined
              return item.job_number === jobNumber &&
                (item.pipe_name == null || item.pipe_name === '' || item.pipe_name === 'null') &&
                item.pass_group_name === passGroup;
            }
          });

          if (passGroupData && passGroupData.length > 0) {
            // Use the first item's data for this pass group
            const firstItem = passGroupData[0];

            // Map parameters to the exact format from the working curl
            parameters.forEach(param => {
              // Convert parameter names to match the working curl format using dynamic mapping
              const paramKey = getParameterKey(param.parameter);

              if (paramKey) {
                // Always use the current component state values for consistency
                const passGroupKey = passGroup.toLowerCase().replace(/\s+/g, '');
                const minKey = `${passGroupKey}Min`;
                const maxKey = `${passGroupKey}Max`;

                let minValue = param[minKey];
                let maxValue = param[maxKey];


                // If no values in component state, fallback to API data
                if (!minValue && minValue !== '0') {
                  if (paramKey === 'current') {
                    minValue = firstItem.current_min;
                  } else if (paramKey === 'volts') {
                    minValue = firstItem.volts_min;
                  } else if (paramKey === 'wire_speed') {
                    minValue = firstItem.wire_speed_min;
                  } else if (paramKey === 'travel_speed') {
                    minValue = firstItem.travel_speed_min;
                  } else if (paramKey === 'oscillation_width') {
                    minValue = firstItem.oscillation_width_min;
                  } else if (paramKey === 'heat') {
                    minValue = firstItem.heat_min;
                  } else if (paramKey === 'true_energy') {
                    minValue = firstItem.true_energy_min;
                  }
                }

                if (!maxValue && maxValue !== '0') {
                  if (paramKey === 'current') {
                    maxValue = firstItem.current_max;
                  } else if (paramKey === 'volts') {
                    maxValue = firstItem.volts_max;
                  } else if (paramKey === 'wire_speed') {
                    maxValue = firstItem.wire_speed_max;
                  } else if (paramKey === 'travel_speed') {
                    maxValue = firstItem.travel_speed_max;
                  } else if (paramKey === 'oscillation_width') {
                    maxValue = firstItem.oscillation_width_max;
                  } else if (paramKey === 'heat') {
                    maxValue = firstItem.heat_max;
                  } else if (paramKey === 'true_energy') {
                    maxValue = firstItem.true_energy_max;
                  }
                }

                // Ensure we have valid values (not null/undefined)
                if (minValue === null || minValue === undefined) {
                  minValue = '0'; // Default fallback as string
                }
                if (maxValue === null || maxValue === undefined) {
                  maxValue = '0'; // Default fallback as string
                }

                // Convert values to API format consistently
                let finalMinValue, finalMaxValue;

                if (typeof minValue === 'string') {
                  finalMinValue = formatValueForAPI(parseFloat(minValue), param.parameter);
                } else {
                  finalMinValue = minValue;
                }

                if (typeof maxValue === 'string') {
                  finalMaxValue = formatValueForAPI(parseFloat(maxValue), param.parameter);
                } else {
                  finalMaxValue = maxValue;
                }

                passUpdate[`${paramKey}_min`] = finalMinValue;
                passUpdate[`${paramKey}_max`] = finalMaxValue;

              }
            });
          } else {
            // If no data found for this pass group, use component state values with fallbacks
            parameters.forEach(param => {
              const paramKey = getParameterKey(param.parameter);

              if (paramKey) {
                const passGroupKey = passGroup.toLowerCase().replace(/\s+/g, '');
                const minKey = `${passGroupKey}Min`;
                const maxKey = `${passGroupKey}Max`;

                let minValue = param[minKey];
                let maxValue = param[maxKey];

                // Ensure we have valid values (not null/undefined)
                if (minValue === null || minValue === undefined) {
                  minValue = '0'; // Default fallback as string
                }
                if (maxValue === null || maxValue === undefined) {
                  maxValue = '0'; // Default fallback as string
                }

                // Convert string values to API format, keep others as-is
                let finalMinValue, finalMaxValue;

                if (typeof minValue === 'string') {
                  finalMinValue = formatValueForAPI(parseFloat(minValue), param.parameter);
                } else {
                  finalMinValue = minValue;
                }

                if (typeof maxValue === 'string') {
                  finalMaxValue = formatValueForAPI(parseFloat(maxValue), param.parameter);
                } else {
                  finalMaxValue = maxValue;
                }

                passUpdate[`${paramKey}_min`] = finalMinValue;
                passUpdate[`${paramKey}_max`] = finalMaxValue;

              }
            });
          }

          return passUpdate;
        });

        // Create selected parameter list with exact format from working curl
        // Always use current UI checkbox states for consistency across all jobs
        const selectedParameterList: any = {};

        // Use current UI checkbox states for all jobs to ensure consistency
        parameters.forEach(param => {
          const paramKey = getParameterApiKey(param.parameter);
          if (paramKey) {
            selectedParameterList[paramKey] = param.enabled;
          }
        });

        return {
          welding_system: jobData?.welding_system || 'p625',
          pipe_name: jobData?.pipe_name || null,
          job_number: jobNumber,
          slope_in: parseInt(slopeTimeIn) || 0,
          slope_out: parseInt(slopeTimeOut) || 0,
          status_calculation_method: statusCalculationMethod,
          status_calculation_level: calculationLevel,
          selected_parameter_list: selectedParameterList,
          pass_updates: passUpdates
        };
      });

      // console.log('payload', payload);
      // Format payload to match the working curl command exactly
      const formattedPayload = {
        payload: payload
      };

      // Call the API - Use centralized project data
      let finalProjectId: number;

      if (isProjectReady && projectId) {
        finalProjectId = Number(projectId);
      } else if (project?.project?.projectId) {
        // Fallback to original project ID if centralized data not ready
        finalProjectId = Number(project.project.projectId);
      } else {
        throw new Error('Project ID not found');
      }
      //  console.log('finalProjectId', finalProjectId);

      const result = updateStatusConfiguration(finalProjectId, formattedPayload);

      const apiResult = await result;

      // Reset editing state
      setIsEditing(false);

      // Call onSave callback if provided
      if (onSave) {
        onSave();
      }

      // Show success message
      Notification.success('Status Configuration saved successfully!');

      setIsDirty(false);

      // Clear loaded jobs to force fresh data load on next access
      resetLoadedJobs();

      // Set waiting state and reload limits data
      setWaitingForReload(true);

      if (onSaveComplete) {
        onSaveComplete();
      }

    } catch (error) {
      console.error('🔍 SAVE VALUES - Error during save:', error);
      Notification.error(`Error saving status configuration: ${error instanceof Error ? error.message : 'Unknown error'}`);

      // Reset waiting state on error
      setWaitingForReload(false);

      // Call onSaveComplete callback even on error to close modal
      if (onSaveComplete) {
        onSaveComplete();
      }
    } finally {
      loadLimitsData(); // No parameters needed - uses centralized project data
      // Ensure loading state is reset regardless of success or failure
      setIsSaving(false);
      setSaveButtonClicked(false);
      resetUpdateStatus();
    }
  };


  // Function to update loaded jobs state when switching between jobs
  const updateLoadedJobsState = (jobNumber: string) => {
    if (jobNumber && !loadedJobs.has(jobNumber)) {
      setLoadedJobs(prev => new Set(Array.from(prev).concat(jobNumber)));
    }
  };

  const handleReset = () => {
    setParameters(getDynamicParameters());
    setSlopeTimeIn('0');
    setSlopeTimeOut('0');
    setStatusCalculationMethod('Average Zone');
    setCalculationLevel('Pass Level');
  };

  const toggleEdit = () => {
    setIsEditing(!isEditing);
    // Clear validation errors when starting to edit
    if (!isEditing) {
      setValidationErrors([]);
      setShowValidationModal(false);
    }
  };

  // Add custom scrollbar styles
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .status-config-table::-webkit-scrollbar {
        height: 8px;
      }
      .status-config-table::-webkit-scrollbar-track {
        background: #f3f4f6;
        border-radius: 4px;
      }
      .status-config-table::-webkit-scrollbar-thumb {
        background: #d1d5db;
        border-radius: 4px;
      }
      .status-config-table::-webkit-scrollbar-thumb:hover {
        background: #9ca3af;
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);



  if (limitsLoading || weldDataLoading || projectsLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-[#EB1C37]" />
        <span className="ml-2 text-gray-600">Loading status configuration data...</span>
      </div>
    );
  }

  // Show empty state when no data is available
  if (!selectedJobNumber || !filteredData.length || uniquePassGroups.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4">
        <div className="text-center">
          <Settings className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Status Configuration Data</h3>
          <p className="text-gray-600 mb-4">
            {!selectedJobNumber
              ? "Please select a job number to view status configuration data."
              : "No pass groups found for the selected job number. Please check if the job has any welding data."
            }
          </p>
          {!selectedJobNumber && jobNumbers.length > 0 && (
            <p className="text-sm text-gray-500">
              Available job numbers: {jobNumbers.join(', ')}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Validation Error Modal */}
      <ValidationErrorModal
        isOpen={showValidationModal}
        onClose={() => setShowValidationModal(false)}
        errors={validationErrors}
        jobNumber={selectedJobNumber || 'N/A'}
        calculationLevel={calculationLevel}
        onErrorsResolved={(resolvedErrors: ValidationError[]) => {
          // Remove resolved errors from validation errors
          const remainingErrors = validationErrors.filter(error =>
            !resolvedErrors.some((resolved: ValidationError) =>
              resolved.parameter === error.parameter &&
              resolved.passGroup === error.passGroup &&
              resolved.type === error.type
            )
          );
          setValidationErrors(remainingErrors);

          // Close modal if no more errors
          if (remainingErrors.length === 0) {
            setShowValidationModal(false);
          }
        }}
      />

      <div className="space-y-4 w-full max-w-full min-w-0">
        {/* Configuration Card — all selectors in a single row with Save on the right */}
        <Card className="border border-[#E2E5EA] shadow-sm max-w-full overflow-visible">
          <CardContent className="p-4 overflow-visible">
            <h3 className="text-[14px] font-semibold text-blueprint-blue mb-4">Configuration</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {/* Job Number */}
              <div className="space-y-1.5 min-w-0">
                <Label className="text-[12px] font-medium text-text-grey">Job Number</Label>
                <Select
                  value={selectedJobNumber}
                  onValueChange={handleJobNumberChange}
                  disabled={!canEdit || limitsLoading || weldDataLoading || projectsLoading}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select job number" />
                  </SelectTrigger>
                  <SelectContent>
                    {jobNumbers.map((jobOption) => (
                      <SelectItem key={jobOption.value} value={jobOption.value}>
                        {jobOption.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Status Calculation Method */}
              <div className="space-y-1.5 min-w-0">
                <Label className="text-[12px] font-medium text-text-grey">Status Calculation Method</Label>
                <Select
                  value={statusCalculationMethod}
                  onValueChange={handleStatusCalculationMethodChange}
                  disabled={!canEdit || !isEditing}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Instantaneous">Instantaneous</SelectItem>
                    <SelectItem value="AvgByPass">Average Pass</SelectItem>
                    <SelectItem value="AvgByZone">Average Zone</SelectItem>
                    <SelectItem value="AvgByTilt">Average Tilt</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Calculation Level */}
              <div className="space-y-1.5 min-w-0">
                <Label className="text-[12px] font-medium text-text-grey">Calculation Level</Label>
                <Select
                  value={calculationLevel}
                  onValueChange={handleCalculationLevelChange}
                  disabled={!canEdit || !isEditing}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pass Level">Pass Level</SelectItem>
                    <SelectItem value="Zone Level">Zone Level</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Slope Time (In / Out inline) */}
              <div className="space-y-1.5 min-w-0">
                <Label className="text-[12px] font-medium text-text-grey">Slope Time (sec)</Label>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5 flex-1">
                    <span className="text-[11px] text-heritage-grey">In</span>
                    <Input
                      id="slopeTimeIn"
                      type="number"
                      value={slopeTimeIn}
                      onChange={(e) => handleSlopeTimeInChange(e.target.value)}
                      className="text-center text-[12px] h-9"
                      placeholder="0"
                      disabled={!canEdit || !isEditing}
                      min="-10"
                      max="10"
                      step="0.1"
                    />
                  </div>
                  <div className="flex items-center gap-1.5 flex-1">
                    <span className="text-[11px] text-heritage-grey">Out</span>
                    <Input
                      id="slopeTimeOut"
                      type="number"
                      value={slopeTimeOut}
                      onChange={(e) => handleSlopeTimeOutChange(e.target.value)}
                      className="text-center text-[12px] h-9"
                      placeholder="0"
                      disabled={!canEdit || !isEditing}
                      min="-10"
                      max="10"
                      step="0.1"
                    />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Status Configuration Table */}
        <Card className="border border-[#E2E5EA] shadow-sm max-w-full overflow-hidden" style={{ maxWidth: '100%' }}>
          <CardContent className="p-0 max-w-full overflow-hidden" style={{ maxWidth: '100%' }}>
            <div className="px-4 py-3 bg-page-bg border-b border-[#E2E5EA]">
              <h3 className="text-[14px] font-semibold text-blueprint-blue">Status Configuration Parameters</h3>
            </div>
            {/* Table Section */}
            <div className="flex flex-col w-full max-w-full overflow-hidden">
              <div className="w-full overflow-x-auto overflow-y-visible table-scroll" style={{ maxWidth: '100%' }}>
                <div className="inline-flex min-w-max">
                  {/* Fixed columns */}
                  <div className="sticky left-0 z-20 bg-white shadow-lg">
                    <table className="border-collapse h-full">
                      <thead className="sticky top-0 bg-white z-10">
                        <tr className="border-b border-[#E2E5EA] h-[50px] sm:h-[60px] bg-page-bg">
                          <th className="w-[140px] xs:w-[160px] sm:w-[180px] md:w-[220px] px-1 xs:px-2 sm:px-4 text-left text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                            <div className="flex items-center gap-1 sm:gap-2">
                              <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
                              <span className="hidden xs:inline">Parameter</span>
                              <span className="xs:hidden">Param</span>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {parameters.map((param, index) => (
                          <tr key={param.id} className={`h-[48px] sm:h-[52px] ${index % 2 === 0 ? 'bg-white' : 'bg-[#FAFBFC]'} ${index < parameters.length - 1 ? 'border-b border-[#EEF0F3]' : ''}`}>
                            <td className="px-1 xs:px-2 sm:px-4 text-left border-r border-[#E2E5EA]">
                              <div className="flex items-center gap-2 sm:gap-3">
                                <Checkbox
                                  id={`checkbox-${param.id}`}
                                  checked={param.enabled}
                                  onCheckedChange={(checked) => handleParameterChange(param.id, 'enabled', checked)}
                                  disabled={!canEdit || !isEditing}
                                  className="data-[state=checked]:bg-hero-red data-[state=checked]:border-hero-red data-[state=checked]:text-white h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0"
                                />
                                <span className="text-[12px] text-text-grey truncate">{param.parameter}</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Scrollable parameters section */}
                  <div className="overflow-x-auto flex-1 min-w-0 w-full" style={{ maxWidth: 'calc(100vw - 240px)' }}>
                    <table className="border-collapse h-full min-w-max">
                      <thead className="sticky top-0 bg-white z-10">
                        <tr className="border-b border-[#E2E5EA] h-[50px] sm:h-[60px] bg-page-bg">
                          {uniquePassGroups.map((passName, index) => (
                            <th key={passName} className={`px-1 xs:px-2 sm:px-4 min-w-[140px] xs:min-w-[160px] sm:min-w-[180px] md:min-w-[240px] ${index === 0 ? '' : 'border-l border-[#E2E5EA]'}`}>
                              <div className="h-[28px] sm:h-[36px] flex items-center justify-center text-center text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                                <div className="flex items-center gap-1">
                                  <Target className="h-3 w-3 sm:h-4 sm:w-4" />
                                  <span className="truncate">{passName}</span>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-1 sm:gap-2 text-[10px] font-bold uppercase tracking-wider text-center text-heritage-grey">
                                <div className="py-1">Min</div>
                                <div className="py-1">Max</div>
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {parameters.map((param, rowIndex) => (
                          <tr key={`${param.id}-params`} className={`h-[48px] sm:h-[52px] ${rowIndex % 2 === 0 ? 'bg-white' : 'bg-[#FAFBFC]'} ${rowIndex < parameters.length - 1 ? 'border-b border-[#EEF0F3]' : ''}`}>
                            {uniquePassGroups.map((passGroup, colIndex) => {
                              const passGroupKey = passGroup.toLowerCase().replace(/\s+/g, '');
                              const minKey = `${passGroupKey}Min`;
                              const maxKey = `${passGroupKey}Max`;

                              // Helper function to check if value should be displayed (not 0, '0', null, undefined, or empty)
                              const shouldDisplayValue = (value: any): boolean => {
                                return value !== null && value !== undefined && value !== '' && value !== 0 && value !== '0';
                              };

                              const minValue = shouldDisplayValue(param[minKey]) ? String(param[minKey]) : '';
                              const maxValue = shouldDisplayValue(param[maxKey]) ? String(param[maxKey]) : '';

                              return (
                                <td key={passGroup} className={`px-1 xs:px-2 sm:px-4 py-1 sm:py-2 h-[48px] sm:h-[52px] text-xs ${colIndex === 0 ? '' : 'border-l border-[#E2E5EA]'}`}>
                                  <div className="grid grid-cols-2 gap-1 sm:gap-3 h-full">
                                    <div className="flex items-center justify-center h-full">
                                      <Input
                                        type="number"
                                        value={minValue}
                                        onChange={(e) => handleParameterChange(param.id, minKey, e.target.value)}
                                        disabled={!canEdit || !isEditing || !param.enabled}
                                        className={`w-full text-center text-xs sm:text-sm disabled:bg-gray-100 disabled:text-gray-500 ${hasInputError(param.parameter, passGroup, 'min')
                                            ? ' focus:ring-red-500 bg-red-50'
                                            : 'border-[#E2E5EA] focus:ring-hero-red/30'
                                          }`}
                                        step="0.01"
                                        placeholder="Min"
                                      />
                                    </div>
                                    <div className="flex items-center justify-center h-full">
                                      <Input
                                        type="number"
                                        value={maxValue}
                                        onChange={(e) => handleParameterChange(param.id, maxKey, e.target.value)}
                                        disabled={!canEdit || !isEditing || !param.enabled}
                                        className={`w-full text-center text-xs sm:text-sm disabled:bg-gray-100 disabled:text-gray-500 ${hasInputError(param.parameter, passGroup, 'max')
                                            ? ' focus:ring-red-500 bg-red-50'
                                            : 'border-[#E2E5EA] focus:ring-hero-red/30'
                                          }`}
                                        step="0.01"
                                        placeholder="Max"
                                      />
                                    </div>
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};